
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const usePatientData = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async (patientId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Vérifier si la relation existe et appartient au professionnel connecté
      const { data: relation, error: relationError } = await supabase
        .from("professional_patients")
        .select("id")
        .eq("patient_id", patientId)
        .eq("professional_id", user.id)
        .single();

      if (relationError || !relation) {
        throw new Error("Relation non trouvée ou non autorisée");
      }

      // Supprimer la relation professionnel-patient
      const { error: deleteRelationError } = await supabase
        .from("professional_patients")
        .delete()
        .eq("id", relation.id);

      if (deleteRelationError) throw deleteRelationError;

      // Supprimer les demandes d'accès associées
      const { error: deleteAccessError } = await supabase
        .from("professional_patient_access")
        .delete()
        .eq("professional_id", user.id)
        .eq("patient_id", patientId);

      if (deleteAccessError) throw deleteAccessError;

      // Créer une notification pour informer le patient
      const { error: notificationError } = await supabase
        .from("notifications")
        .insert({
          user_id: patientId,
          title: "Accès supprimé",
          content: "Un professionnel de santé a supprimé son accès à vos données médicales.",
          type: "access_removed"
        });

      if (notificationError) {
        console.error("Erreur lors de la création de la notification:", notificationError);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["professional-patients"] });
      toast({
        title: "Patient supprimé",
        description: "Le patient a été retiré de votre liste",
      });
    },
    onError: (error) => {
      console.error("Erreur lors de la suppression du patient:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le patient",
      });
    },
  });

  return { mutate, isPending };
};
